import { differenceInDays, parseISO, addDays, isBefore, isAfter, subYears } from 'date-fns';
import { Trip, TemporaryResidencyPeriod, CitizenshipEligibility } from '../types';

// Canadian citizenship requirements
const REQUIRED_DAYS = 1095; // 3 years in days
const ELIGIBILITY_PERIOD_YEARS = 5;
const MAX_TEMPORARY_DAYS_COUNTED = 365; // Maximum days from temporary residency that count

/**
 * Calculate citizenship eligibility based on PR date, trips, and temporary residency periods
 */
export function calculateCitizenshipEligibility(
  prDate: string,
  trips: Trip[],
  temporaryPeriods: TemporaryResidencyPeriod[] = []
): CitizenshipEligibility {
  const prDateObj = parseISO(prDate);
  const today = new Date();
  // Start of the 5-year window (immediately before applying)
  const windowStart = subYears(today, ELIGIBILITY_PERIOD_YEARS);
  const windowEnd = today;
  
  // Calculate days in Canada as permanent resident (only within the 5-year window)
  const permanentResidencyDays = calculatePermanentResidencyDays(
    prDateObj,
    windowEnd,
    trips
  );
  
  // Calculate temporary residency days (count as half days, max 365) within window prior to PR date
  const temporaryResidencyDays = Math.min(
    calculateTemporaryResidencyDays(temporaryPeriods, windowStart, prDateObj),
    MAX_TEMPORARY_DAYS_COUNTED
  );
  
  const totalDays = permanentResidencyDays + temporaryResidencyDays;
  const daysRemaining = Math.max(0, REQUIRED_DAYS - totalDays);
  const isEligible = totalDays >= REQUIRED_DAYS;
  
  // Calculate eligibility date
  let eligibilityDate: string | null = null;
  if (!isEligible && daysRemaining > 0) {
    const projectedEligibilityDate = addDays(today, daysRemaining);
    eligibilityDate = projectedEligibilityDate.toISOString().split('T')[0];
  } else if (isEligible) {
    eligibilityDate = today.toISOString().split('T')[0];
  }
  
  return {
    isEligible,
    daysCompleted: totalDays,
    daysRequired: REQUIRED_DAYS,
    eligibilityDate,
    daysRemaining,
    temporaryResidencyDays,
    permanentResidencyDays
  };
}

/**
 * Calculate absence days for a trip based on Canadian citizenship rules
 * - Departure day: counts as absence (not in Canada)
 * - Return day: counts as present (in Canada)
 * - Same-day trip: 0 days absence
 */
function calculateAbsenceDays(departureDate: Date, returnDate: Date): number {
  // If same day, no absence
  if (differenceInDays(returnDate, departureDate) === 0) {
    return 0;
  }
  
  // Departure day counts as absence, return day does not
  // So we exclude the return day from the count
  const absenceDays = differenceInDays(returnDate, departureDate);
  return absenceDays;
}

/**
 * Calculate days in Canada as permanent resident, accounting for trips
 */
function calculatePermanentResidencyDays(
  prDate: Date,
  endDate: Date,
  trips: Trip[]
): number {
  const windowStart = subYears(endDate, ELIGIBILITY_PERIOD_YEARS);
  const effectiveStart = isAfter(prDate, windowStart) ? prDate : windowStart;
  const totalDaysAsPRInWindow = Math.max(0, differenceInDays(endDate, effectiveStart));

  // Subtract days outside Canada (trips that don't count toward residency) within PR window
  const daysOutside = trips
    .filter(trip => !trip.countsTowardResidency)
    .reduce((total, trip) => {
      const departure = parseISO(trip.departureDate);
      const returnDate = parseISO(trip.returnDate);

      // Only count overlap of the trip with the PR period in-window
      const tripStart = isAfter(departure, effectiveStart) ? departure : effectiveStart;
      const tripEnd = isBefore(returnDate, endDate) ? returnDate : endDate;
      
      if (isBefore(tripStart, tripEnd)) {
        // Use proper absence calculation: departure day counts, return day doesn't
        const absenceDays = calculateAbsenceDays(tripStart, tripEnd);
        return total + absenceDays;
      }
      return total;
    }, 0);

  return Math.max(0, totalDaysAsPRInWindow - daysOutside);
}

/**
 * Calculate temporary residency days (count as half days)
 */
function calculateTemporaryResidencyDays(
  temporaryPeriods: TemporaryResidencyPeriod[],
  windowStart: Date,
  prDate: Date
): number {
  // Consider overlap of each temporary period with [windowStart, prDate)
  const totalTempFullDays = temporaryPeriods.reduce((total, period) => {
    const start = parseISO(period.startDate);
    const end = parseISO(period.endDate);
    const overlapStart = isAfter(start, windowStart) ? start : windowStart;
    const overlapEnd = isBefore(end, prDate) ? end : prDate;
    if (!isBefore(overlapStart, overlapEnd)) return total;

    const overlapDays = differenceInDays(overlapEnd, overlapStart);
    // If the period provided a specific daysInCanada, take the minimum of overlap vs recorded
    const credited = Math.min(overlapDays, period.daysInCanada ?? overlapDays);
    return total + credited;
  }, 0);

  // Half-day credit, capped at 365
  return Math.floor(totalTempFullDays / 2);
}

/**
 * Determine if a trip counts toward residency based on purpose and circumstances
 */
export function doesTripCountTowardResidency(trip: Trip): boolean {
  const countingPurposes: string[] = [
    'work_canadian_company',
    'accompanying_canadian_citizen'
  ];
  
  // Check if trip purpose counts OR if accompanied by Canadian spouse
  return countingPurposes.includes(trip.purpose) || trip.accompaniedByCanadianSpouse === true;
}

/**
 * Calculate days between two dates (simple difference)
 */
export function calculateDaysBetween(startDate: string, endDate: string): number {
  return differenceInDays(parseISO(endDate), parseISO(startDate));
}

/**
 * Calculate trip absence days using Canadian citizenship rules
 * Departure day counts as absence, return day doesn't
 * Same-day trips = 0 absence days
 */
export function calculateTripAbsenceDays(departureDate: string, returnDate: string): number {
  return calculateAbsenceDays(parseISO(departureDate), parseISO(returnDate));
}

/**
 * Get the next milestone dates for notifications
 */
export function getNotificationMilestones(eligibilityDate: string | null): Date[] {
  if (!eligibilityDate) return [];
  
  const eligibility = parseISO(eligibilityDate);
  const milestones: Date[] = [];
  
  // Day of
  if (isAfter(eligibility, new Date())) {
    milestones.push(eligibility);
  }

  // 1 week before
  const oneWeekBefore = addDays(eligibility, -7);
  if (isAfter(oneWeekBefore, new Date())) {
    milestones.push(oneWeekBefore);
  }

  // 12 months before
  const twelveMonthsBefore = addDays(eligibility, -365);
  if (isAfter(twelveMonthsBefore, new Date())) {
    milestones.push(twelveMonthsBefore);
  }
  
  // 6 months before
  const sixMonthsBefore = addDays(eligibility, -180);
  if (isAfter(sixMonthsBefore, new Date())) {
    milestones.push(sixMonthsBefore);
  }
  
  // 3 months before
  const threeMonthsBefore = addDays(eligibility, -90);
  if (isAfter(threeMonthsBefore, new Date())) {
    milestones.push(threeMonthsBefore);
  }
  
  return milestones;
}

/**
 * Format days into years, months, and days
 */
export function formatDaysToYearsMonthsDays(days: number): string {
  const years = Math.floor(days / 365);
  const remainingDays = days % 365;
  const months = Math.floor(remainingDays / 30);
  const finalDays = remainingDays % 30;
  
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  if (finalDays > 0) parts.push(`${finalDays} day${finalDays !== 1 ? 's' : ''}`);
  
  return parts.join(', ') || '0 days';
}

/**
 * Calculate progress percentage
 */
export function calculateProgress(completed: number, required: number): number {
  return Math.min(100, Math.round((completed / required) * 100));
}
